import { StatsigClient } from "@statsig/react-bindings";
import { NotificationPermissionState } from "@types";
import { getStoredCredential } from "@utils/credentials";
import logger from "@utils/logger";
import { getReferralCode } from "@utils/referral";
import { GameModel, MemberSettingAuthMode, WalletTxMethod, WithdrawRequest, WithdrawResponse } from "kz-ui-sdk";
import { EVENTS, EventName } from "./constants";
import type { DepositCreateResultOptions } from "./types";
import {
  AuthSource,
  DepositSource,
  DepositStep,
  EventMetadata,
  LanguageChangeSource,
  NotificationAction,
  NotificationBrowserPromptResponseMetadata,
  NotificationInAppPromptResponseMetadata,
  NotificationInAppPromptShownMetadata,
  NotificationInAppSource,
  NotificationSubscriptionStatusMetadata,
  OTPFailedAttemptMetadata,
  OTPMethod,
  OTPSource,
  RegistrationFormFocusMetadata,
  RegistrationMethod,
  RegistrationStep,
} from "./types";
import {
  DepositFunnelTracker,
  RegistrationFunnelTracker,
  getGlobalStatsigClient,
  hasSeenAndMarkDeposit,
  hasSeenAndMarkRegistrationFormFocus,
  isValidEvent,
  waitForStatsigReadyEvent,
} from "./utils";

/**
 * StatsigTracker - usable outside React components
 * Mirrors the API of useStatsigTracking for consistency.
 */
export class StatsigTracker {
  private static instance: StatsigTracker | null = null;

  static getInstance(): StatsigTracker {
    if (!StatsigTracker.instance) {
      StatsigTracker.instance = new StatsigTracker();
    }
    return StatsigTracker.instance;
  }

  private pendingEvents: Array<{ eventName: EventName | string; value?: number; metadata?: Record<string, any> }> = [];
  private initialized = false;

  private get client(): StatsigClient | null {
    return getGlobalStatsigClient();
  }

  /** Attach a one-time readiness listener and flush queue when ready */
  private ensureInitialized(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Kick off readiness wait; flush when ready or timeout
    void waitForStatsigReadyEvent().then(() => {
      this.flushEvents();
    });
  }

  isReady(): boolean {
    const client = this.client as any;
    return !!client && client.loadingStatus === "Ready";
  }

  track(eventName: EventName | string, metadata?: EventMetadata, value?: number): void {
    const client = this.client;

    // Dev warning for non-conforming names (do not block)
    if (
      !isValidEvent(eventName) &&
      !(typeof eventName === "string" && (eventName.startsWith("act:") || eventName.startsWith("evt:")))
    ) {
      logger._console.warn(`Event name "${eventName}" doesn't follow naming convention (act: or evt:)`);
    }

    if (!client) {
      this.pendingEvents.push({ eventName, value, metadata });
      this.ensureInitialized();
      logger._console.warn("Statsig client not available, queueing event");
      return;
    }

    try {
      client.logEvent(eventName, value, metadata || {});
      logger._console.log(`[Statsig] ${eventName}`, { value, metadata });
    } catch (error) {
      logger._console.error("Failed to log Statsig event:", error);
      this.pendingEvents.push({ eventName, value, metadata });
    }
  }

  flushEvents(): void {
    if (!this.client || this.pendingEvents.length === 0) return;
    const eventsToProcess = [...this.pendingEvents];
    this.pendingEvents = [];
    for (const { eventName, value, metadata } of eventsToProcess) {
      this.track(eventName, metadata, value);
    }
  }

  // ===== Registration Tracking =====

  trackRegisterFormFocus(): void {
    if (hasSeenAndMarkRegistrationFormFocus()) return;
    const metadata: RegistrationFormFocusMetadata = {
      pathname: window.location.pathname,
      fromPathname: (window.history?.state as any)?.usr?.from?.pathname ?? undefined,
      search: window.location.search ?? undefined,
    };
    this.track(EVENTS.REGISTRATION_FORM_FOCUS, metadata);
  }

  trackRegisterClick(source: AuthSource): void {
    const referralCode = this.getReferralCodeSafe();
    const tracker = RegistrationFunnelTracker.getInstance();
    tracker.startRegistration();
    tracker.addStep(RegistrationStep.CLICK_REGISTER);

    this.track(EVENTS.CLICK_REGISTER, {
      source,
      step: RegistrationStep.CLICK_REGISTER,
      referralCode,
      hasReferralCode: !!referralCode,
    });
  }

  trackRegisterFormSubmit(
    method: RegistrationMethod,
    options?: {
      timeSpentOnForm?: number;
      retryCount?: number;
    },
  ): void {
    const referralCode = this.getReferralCodeSafe();
    const tracker = RegistrationFunnelTracker.getInstance();
    tracker.addStep(RegistrationStep.FORM_SUBMIT);
    const attemptCount = tracker.incrementAttempt("register_form_submit");

    this.track(EVENTS.REGISTER_FORM_SUBMIT, {
      method,
      step: RegistrationStep.FORM_SUBMIT,
      timeSpentOnForm: options?.timeSpentOnForm,
      retryCount: options?.retryCount || attemptCount,
      referralCode,
      hasReferralCode: !!referralCode,
    });
  }

  trackOTPSent(
    method: string,
    options?: {
      deliveryTime?: number;
      retryCount?: number;
    },
  ): void {
    const tracker = RegistrationFunnelTracker.getInstance();
    tracker.addStep(RegistrationStep.OTP_SENT);
    const attemptCount = tracker.incrementAttempt("otp_sent");

    this.track(EVENTS.OTP_REQUEST_RESULT, {
      method,
      step: RegistrationStep.OTP_SENT,
      deliveryTime: options?.deliveryTime,
      retryCount: options?.retryCount || attemptCount,
      success: true,
    });
  }

  trackOTPVerified(
    method: string,
    options?: {
      attemptsCount?: number;
      timeToVerify?: number;
    },
  ): void {
    const tracker = RegistrationFunnelTracker.getInstance();
    tracker.addStep(RegistrationStep.OTP_VERIFY);

    this.track(EVENTS.OTP_VERIFY_RESULT, {
      method,
      step: RegistrationStep.OTP_VERIFY,
      attemptsCount: options?.attemptsCount || 1,
      timeToVerify: options?.timeToVerify,
      success: true,
    });
  }

  trackOTPRequestFailed(error: any): void {
    const otpTracker = RegistrationFunnelTracker.getInstance();
    const metadata: OTPFailedAttemptMetadata = {
      method: OTPMethod.SMS,
      step: RegistrationStep.OTP_SENT,
      error,
      attemptsCount: otpTracker.incrementAttempt("otp_request"),
    };
    this.track(EVENTS.OTP_REQUEST_RESULT, { ...metadata, success: false });
  }

  trackOTPFailedAttempt(error: any): void {
    const failTracker = RegistrationFunnelTracker.getInstance();
    const metadata = {
      method: OTPMethod.SMS,
      step: RegistrationStep.OTP_VERIFY,
      attemptsCount: failTracker.incrementAttempt("otp_verify_fail"),
      error,
      success: false,
    };
    this.track(EVENTS.OTP_VERIFY_RESULT, metadata);
  }

  trackOTPRequest(method: string, options?: { source?: OTPSource }): void {
    this.track(EVENTS.OTP_REQUEST, {
      method,
      step: RegistrationStep.OTP_SENT,
      source: options?.source,
    });
  }

  async trackRegistrationComplete(
    method: RegistrationMethod,
    options?: {
      apiIdentifier?: string;
      prefix?: string;
    },
  ): Promise<void> {
    try {
      const referralCode = this.getReferralCodeSafe();
      const tracker = RegistrationFunnelTracker.getInstance();
      tracker.addStep(RegistrationStep.REGISTRATION_COMPLETE);

      // Best-effort user update if available
      if (options?.apiIdentifier && this.client) {
        const computedUserID = `${options?.prefix || ""}${options.apiIdentifier}`;
        const anyClient = this.client as any;
        const updateUser = anyClient.updateUserAsync || anyClient.updateUser;
        if (typeof updateUser === "function") {
          const result = await updateUser.call(anyClient, { userID: computedUserID });
          logger._console.log("[Statsig] updateUser result", result);
        }
      }

      this.track(EVENTS.REGISTRATION_RESULT, {
        method,
        step: RegistrationStep.REGISTRATION_COMPLETE,
        hasReferralCode: !!referralCode,
        referralCode,
        totalTimeToComplete: Math.floor(tracker.getTotalTime() / 1000),
        stepsCompleted: tracker.getCompletedSteps(),
        success: true,
      });
    } catch (error) {
      logger._console.error("Failed to complete registration tracking:", error);
    }
  }

  trackRegistrationFailed(method: RegistrationMethod, error: any): void {
    const referralCode = getReferralCode();

    const tracker = RegistrationFunnelTracker.getInstance();
    this.track(EVENTS.REGISTRATION_RESULT, {
      method,
      hasReferralCode: !!referralCode,
      referralCode,
      success: false,
      error,
      totalTimeToComplete: Math.floor(tracker.getTotalTime() / 1000),
    });
  }

  // ===== General/Auth Tracking =====

  trackLoginClick(source: AuthSource): void {
    this.track(EVENTS.CLICK_LOGIN, { source });
  }

  trackAuthClick(source: AuthSource, requireRegister: boolean = true): void {
    const hasSavedPhoneNumber = getStoredCredential()?.clientId;
    if (hasSavedPhoneNumber) {
      this.trackLoginClick(source);
    } else {
      if (requireRegister) {
        this.trackRegisterClick(source);
      } else {
        this.trackLoginClick(source);
      }
    }
  }

  trackLogin(
    authMode: MemberSettingAuthMode,
    options?: {
      grantType?: "password" | "otp";
    },
  ): void {
    this.track(EVENTS.LOGIN, {
      authMode,
      grantType: options?.grantType,
    });
  }

  trackChangeLanguage(from: string, to: string, source: LanguageChangeSource): void {
    if (from === to) return;
    this.track(EVENTS.CHANGE_LANGUAGE, { from, to, source });
  }

  // ===== Deposit Tracking =====

  trackDepositClick(
    source: DepositSource,
    options?: {
      accountBalance?: number;
    },
  ): void {
    const tracker = DepositFunnelTracker.getInstance();
    tracker.startDeposit();
    tracker.addStep(DepositStep.CLICK_DEPOSIT);

    this.track(EVENTS.CLICK_DEPOSIT, {
      source,
      previousPage: window.location.pathname,
      accountBalance: options?.accountBalance,
    });
  }

  trackDepositMethodGroup(groupId: string, groupName: string, source: DepositSource): void {
    const tracker = DepositFunnelTracker.getInstance();
    tracker.addStep(DepositStep.SELECT_GROUP);
    tracker.setSelectedData("groupId", groupId);
    tracker.setSelectedData("groupName", groupName);

    this.track(EVENTS.SELECT_DEPOSIT_METHOD_GROUP, {
      source,
      step: DepositStep.SELECT_GROUP,
      groupId,
      groupName,
    });
  }

  trackDepositMethodIndividual(
    methodId: string,
    methodName: string,
    options?: {
      source?: DepositSource;
      methodKey?: string;
      groupId?: string;
      isRecommended?: boolean;
      fees?: number;
      processingTime?: string;
    },
  ): void {
    const tracker = DepositFunnelTracker.getInstance();
    tracker.addStep(DepositStep.SELECT_METHOD);
    tracker.setSelectedData("methodId", methodId);
    tracker.setSelectedData("methodName", methodName);

    this.track(EVENTS.SELECT_DEPOSIT_METHOD_INDIVIDUAL, {
      source: options?.source,
      step: DepositStep.SELECT_METHOD,
      methodId,
      methodName,
      methodKey: options?.methodKey,
      groupId: options?.groupId,
      isRecommended: options?.isRecommended,
      fees: options?.fees,
      processingTime: options?.processingTime,
    });
  }

  trackAutoFastTriggered(
    autoSelectedMethod: string,
    options?: {
      source?: DepositSource;
      originalMethod?: string;
      reason?: string;
      timeSaved?: string;
    },
  ): void {
    this.track(EVENTS.AUTO_FAST_TRIGGERED, {
      source: options?.source,
      step: DepositStep.SELECT_METHOD,
      originalMethod: options?.originalMethod,
      autoSelectedMethod,
      reason: options?.reason || "auto_selection",
      timeSaved: options?.timeSaved,
    });
  }

  trackDepositAmountSelect(
    amount: number,
    options?: {
      source?: DepositSource;
      selectedMethod?: string;
      isQuickAmount?: boolean;
      bonusOffered?: number;
      currency?: string;
    },
  ): void {
    const tracker = DepositFunnelTracker.getInstance();
    tracker.addStep(DepositStep.SELECT_AMOUNT);
    tracker.setSelectedData("amount", amount);

    this.track(EVENTS.SELECT_DEPOSIT_AMOUNT, {
      source: options?.source,
      step: DepositStep.SELECT_AMOUNT,
      amount,
      selectedMethod: options?.selectedMethod,
      bonusOffered: options?.bonusOffered,
      currency: options?.currency,
    });
  }

  trackTopUpFailed(error: any, options?: DepositCreateResultOptions): void {
    this.track(EVENTS.DEPOSIT_CREATE_RESULT, {
      source: options?.source,
      currency: options?.currency,
      error,
      success: false,
      methodId: options?.methodId,
      methodName: options?.methodName,
      orderRef: options?.orderRef,
      depositGroupId: options?.depositGroupId,
      depositGroupName: options?.depositGroupName,
      methodKey: options?.methodKey,
      amount: options?.amount,
      providerKey: options?.providerKey,
      paymentMethod: options?.paymentMethod,
    });
  }

  trackTopUpSubmit(
    amount: number,
    methodId: string,
    methodName: string,
    options?: {
      source?: DepositSource;
      currency?: string;
      estimatedProcessingTime?: string;
    },
  ): void {
    const tracker = DepositFunnelTracker.getInstance();
    tracker.addStep(DepositStep.SUBMIT_TOP_UP);

    this.track(EVENTS.TOP_UP_SUBMIT, {
      source: options?.source,
      step: DepositStep.SUBMIT_TOP_UP,
      amount,
      methodId,
      methodName,
      currency: options?.currency,
      estimatedProcessingTime: options?.estimatedProcessingTime,
    });
  }

  trackDepositSuccess(
    amount: number,
    transactionId: string,
    options?: {
      source?: DepositSource;
      method?: string;
      currency?: string;
      actualProcessingTime?: number;
      methodId?: string;
      providerKey?: string;
    },
  ): void {
    // De-duplicate across sources by transactionId/orderRef
    if (hasSeenAndMarkDeposit(transactionId)) {
      logger._console.log("[Statsig] Skipping duplicate deposit success track for", transactionId);
      return;
    }

    const tracker = DepositFunnelTracker.getInstance();
    tracker.addStep(DepositStep.DEPOSIT_COMPLETE);

    this.track(EVENTS.DEPOSIT_SUCCESS, {
      source: options?.source,
      step: DepositStep.DEPOSIT_COMPLETE,
      amount,
      method: options?.method,
      orderRef: transactionId,
      providerKey: options?.providerKey,
      currency: options?.currency,
      actualProcessingTime: options?.actualProcessingTime,
      totalDepositTime: Math.floor(tracker.getTotalTime() / 1000),
    });
  }

  trackDepositFailed(
    amount: number,
    methodId: string,
    error: any,
    options?: {
      source?: DepositSource;
      methodName?: string;
      currency?: string;
      retryAttempt?: number;
      failurePoint?: string;
    },
  ): void {
    this.track(EVENTS.DEPOSIT_FAILED, {
      source: options?.source,
      step: DepositStep.SUBMIT_DEPOSIT,
      amount,
      methodId,
      methodName: options?.methodName,
      currency: options?.currency,
      error,
      retryAttempt: options?.retryAttempt || 1,
      failurePoint: options?.failurePoint,
    });
  }

  trackDepositCreateSuccess(amount: number, options?: DepositCreateResultOptions): void {
    this.track(EVENTS.DEPOSIT_CREATE_RESULT, {
      source: options?.source,
      currency: options?.currency,
      success: true,
      amount,
      methodId: options?.methodId,
      methodName: options?.methodName,
      orderRef: options?.orderRef,
      depositGroupId: options?.depositGroupId,
      depositGroupName: options?.depositGroupName,
      methodKey: options?.methodKey,
      providerKey: options?.providerKey,
      paymentMethod: options?.paymentMethod,
    });
  }

  // ===== Gameplay Tracking =====

  trackGameOpened(game: Partial<GameModel>, options?: { source?: string; searchTerm?: string }): void {
    this.track(EVENTS.GAME_OPENED, { game, source: options?.source, searchTerm: options?.searchTerm });
  }

  trackGameOpenTriggered(game: Partial<GameModel>, options?: { source?: string; searchTerm?: string }): void {
    this.track(EVENTS.GAME_OPEN_TRIGGERED, { game, source: options?.source, searchTerm: options?.searchTerm });
  }

  trackGameSearch(searchTerm: string | undefined, options?: { resultCount?: number }): void {
    this.track(EVENTS.GAME_SEARCH, { searchTerm, resultCount: options?.resultCount });
  }

  trackGameSearchSelect(game: Partial<GameModel>, options?: { source?: string; searchTerm?: string }): void {
    this.track(EVENTS.GAME_SEARCH_SELECT, { game, source: options?.source, searchTerm: options?.searchTerm });
  }

  trackGameClosed(game: Partial<GameModel>): void {
    this.track(EVENTS.GAME_CLOSED, { game });
  }

  trackGameError(error: any): void {
    this.track(EVENTS.GAME_ERROR, { error });
  }

  trackSessionTimeout(): void {
    this.track(EVENTS.SESSION_TIMEOUT, {});
  }

  // ===== Promotion Tracking =====

  trackPromoClaimAttempt(promoType: WalletTxMethod): void {
    this.track(EVENTS.PROMO_CLAIM_ATTEMPT, { promoType });
  }

  trackPromoClaimSuccess(promoType: WalletTxMethod): void {
    this.track(EVENTS.PROMO_CLAIM_SUCCESS, { promoType });
  }

  trackPromoRewardApplied(promoId: string): void {
    this.track(EVENTS.PROMO_REWARD_APPLIED, { promoId });
  }

  trackPromoBonusExpired(promoId: string): void {
    this.track(EVENTS.PROMO_BONUS_EXPIRED, { promoId });
  }

  // ===== Withdrawal Tracking =====

  trackWithdrawalClick(): void {
    this.track(EVENTS.CLICK_WITHDRAWAL, {});
  }

  trackWithdrawalSubmit(payload: WithdrawRequest): void {
    this.track(EVENTS.WITHDRAWAL_SUBMIT, { payload });
  }

  trackWithdrawalSuccess(response: WithdrawResponse): void {
    this.track(EVENTS.WITHDRAWAL_SUCCESS, { response });
  }

  trackWithdrawalFailed(error: any): void {
    this.track(EVENTS.WITHDRAWAL_FAILED, { error });
  }

  // ===== Notifications Tracking =====

  trackNotificationInAppPromptShown(source: NotificationInAppSource = NotificationInAppSource.PRIZES_PAGE): void {
    const metadata: NotificationInAppPromptShownMetadata = { source };
    this.track(EVENTS.NOTIFICATION_INAPP_PROMPT_SHOWN, metadata);
  }

  trackNotificationInAppPromptResponse(action: NotificationAction): void {
    const metadata: NotificationInAppPromptResponseMetadata = { action };
    this.track(EVENTS.NOTIFICATION_INAPP_PROMPT_RESPONSE, metadata);
  }

  trackNotificationBrowserPromptShown(): void {
    this.track(EVENTS.NOTIFICATION_BROWSER_PROMPT_SHOWN, {});
  }

  trackNotificationBrowserPromptResponse(action: NotificationAction): void {
    const metadata: NotificationBrowserPromptResponseMetadata = { action };
    this.track(EVENTS.NOTIFICATION_BROWSER_PROMPT_RESPONSE, metadata);
  }

  trackNotificationSubscriptionStatus(status: NotificationPermissionState): void {
    const metadata: NotificationSubscriptionStatusMetadata = { status };
    this.track(EVENTS.NOTIFICATION_SUBSCRIPTION_STATUS, metadata);
  }

  // ===== Utilities =====

  private getReferralCodeSafe(): string | undefined {
    try {
      return getReferralCode();
    } catch {
      return undefined;
    }
  }
}
